// managers/aircraft/AircraftManager.ts
import type { Map as MapType, GeoJSONSource } from "maplibre-gl";
import bearing from "@turf/bearing";
import { point } from "@turf/helpers";
import type { MapEventBus } from "../../core/MapEventBus";
import { SOURCES } from "../../sources";
import { AircraftSelection } from "./AircraftSelection";
import type { SkopaTarget } from "../../types";
import { AircraftFilterEngine } from "./AircraftFilterEngine";

const MAX_HISTORY = 40;
const TRAIL_APPEND_INTERVAL_MS = 1000;

export class AircraftManager {
  private aircraftCache = new Map<string, SkopaTarget>();
  private pendingUpdates = new Set<string>();
  private tracks = new Map<string, [number, number][]>();
  private lastTrailAppendTime = new Map<string, number>();
  private selection: AircraftSelection;

  private featurePool = new Map<string, GeoJSON.Feature>();
  private trailPool = new Map<string, GeoJSON.Feature>();
  private shadowFeatureStateHashes = new Map<string, string>();
  private currentlySelectedIds = new Set<string>();

  private numericIdMap = new Map<string, number>();
  private nextNumericId = 1;
  private animationFrameId: number | null = null;
  private lastTrailsFlushTime = 0;

  public filterEngine = new AircraftFilterEngine();
  public readyPromise: Promise<AircraftFilterEngine>;

  constructor(
    private map: MapType,
    private eventBus: MapEventBus,
  ) {
    this.selection = new AircraftSelection(eventBus);
    this.setupListeners();
    this.startUpdateLoop();
    this.readyPromise = Promise.resolve(this.filterEngine);
  }

  private setupListeners() {
    this.eventBus.on("aircraft:selected", ({ track_id }) => {
      if (!track_id) return;
      this.currentlySelectedIds.add(track_id);
      this.pendingUpdates.add(track_id);
    });

    this.eventBus.on("aircraft:deselected", ({ track_id }) => {
      if (!track_id) return;
      this.currentlySelectedIds.delete(track_id);
      this.pendingUpdates.add(track_id);
    });
  }

  private getNumericId(trackId: string): number {
    let id = this.numericIdMap.get(trackId);
    if (id === undefined) {
      id = this.nextNumericId++;
      this.numericIdMap.set(trackId, id);
    }
    return id;
  }

  private startUpdateLoop() {
    const loop = () => {
      if (this.pendingUpdates.size > 0) {
        this.flushUpdates();
      }
      this.animationFrameId = requestAnimationFrame(loop);
    };
    this.animationFrameId = requestAnimationFrame(loop);
  }

  updateAircraftBatch(targets: SkopaTarget[]) {
    const now = Date.now();

    const incomingIds = new Set(targets.map((t) => t.track_id));

    for (const track_id of this.aircraftCache.keys()) {
      if (!incomingIds.has(track_id)) {
        this.removeAircraft(track_id);
      }
    }

    for (const target of targets) {
      this.updateTracks(
        target.track_id,
        [target.position.lon, target.position.lat],
        now,
      );
      this.aircraftCache.set(target.track_id, target);
      this.pendingUpdates.add(target.track_id);
    }
    this.eventBus.emit("data:aircraft-batch-updated", { targets });
  }

  selectAircraft(track_id: string) {
    if (!track_id) return;
    this.currentlySelectedIds.add(track_id);
    this.selection.selectAircraft(track_id);
    this.pendingUpdates.add(track_id);
    this.notifySelectedAircraftChanged();
  }

  deselectAircraft(track_id: string) {
    if (!track_id) return;
    this.currentlySelectedIds.delete(track_id);
    this.selection.deselectAircraft(track_id);
    this.pendingUpdates.add(track_id);
    this.notifySelectedAircraftChanged();
  }

  clearAllSelections() {
    for (const track_id of this.currentlySelectedIds) {
      this.pendingUpdates.add(track_id);
    }
    this.currentlySelectedIds.clear();
    this.notifySelectedAircraftChanged();
  }

  private flushUpdates() {
    if (!this.map || !this.map.isStyleLoaded()) return;

    const aircraftSource = this.map.getSource(
      SOURCES.AIRCRAFT,
    ) as GeoJSONSource;
    const trailsSource = this.map.getSource(SOURCES.TRAILS) as GeoJSONSource;
    if (!aircraftSource || !trailsSource) return;

    this.pendingUpdates.clear();

    // 🌟 EDGE CASE SWEEP: If cache was completely wiped by a macro filter command,
    // immediately force push empty array collections to both WebGL assets.
    if (this.aircraftCache.size === 0) {
      try {
        aircraftSource.setData({ type: "FeatureCollection", features: [] });
        trailsSource.setData({ type: "FeatureCollection", features: [] });
        this.lastTrailsFlushTime = Date.now();
      } catch (err) {
        console.warn("MapLibre flush sync deferred:", err);
      }
      return;
    }

    const aircraftFeatures: GeoJSON.Feature[] = [];
    const trailsFeatures: GeoJSON.Feature[] = [];
    const now = Date.now();

    let pointsSourceNeedsSync = false;
    const shouldFlushTrails = now - this.lastTrailsFlushTime >= 333;

    const bounds = this.map.getBounds();
    const west = bounds.getWest();
    const east = bounds.getEast();
    const north = bounds.getNorth();
    const south = bounds.getSouth();

    for (const [track_id, target] of this.aircraftCache) {
      const lon = target.position.lon;
      const lat = target.position.lat;

      const isVisible =
        lon >= west && lon <= east && lat >= south && lat <= north;
      if (!isVisible) continue;

      const track = this.tracks.get(target.track_id);
      let rawBearing =
        target.heading_deg && !Number.isNaN(Number(target.heading_deg))
          ? Number(target.heading_deg)
          : Number(this.getTrackBearing(track));

      if (Number.isNaN(rawBearing)) rawBearing = 0;

      let feature = this.featurePool.get(track_id);
      const numericId = this.getNumericId(track_id);
      const isSelected = this.currentlySelectedIds.has(track_id);

      const topKvp = (target.kvp ?? []).slice(0, 3);

      const labelText = topKvp
        .filter((kv) => kv.key && kv.value)
        .map((kv) => `${kv.key}: ${kv.value}`)
        .join("\n");

      const stateHash = `${lon.toFixed(4)},${lat.toFixed(4)},${rawBearing.toFixed(1)},${isSelected}`;
      const existingHash = this.shadowFeatureStateHashes.get(track_id);

      if (!feature) {
        feature = {
          id: numericId,
          type: "Feature",
          geometry: { type: "Point", coordinates: [lon, lat] },
          properties: {},
        };
        this.featurePool.set(track_id, feature);
        pointsSourceNeedsSync = true;
      }

      if (existingHash !== stateHash) {
        feature.geometry = { type: "Point", coordinates: [lon, lat] };
        feature.properties! = {
          track_id: target.track_id,
          numeric_id: numericId,
          callsign: target.callsign || "",
          intent: target.intent || "unknown",
          icon: target.icon || "default",
          sprite_key: `${target.intent || "unknown"}-${target.icon || "default"}`,
          is_selected: isSelected,
          altitude_ft: Number(target.altitude_ft || 0),
          heading_deg: rawBearing,
          label_text: labelText,
          speed_knots: Number(target.speed_knots || 0),
          standard_identity: target.standard_identity || "u",
          environment: target.environment || "a",
        };

        this.shadowFeatureStateHashes.set(track_id, stateHash);
        pointsSourceNeedsSync = true;
      }

      aircraftFeatures.push(feature);

      if (shouldFlushTrails && track && track.length > 0) {
        let trailFeature = this.trailPool.get(track_id);
        const activeRenderPath = [...track, [lon, lat] as [number, number]];

        if (!trailFeature) {
          trailFeature = {
            type: "Feature",
            geometry: { type: "LineString", coordinates: activeRenderPath },
            properties: {
              color: "white",
              standard_identity: target.standard_identity || "u",
              environment: target.environment || "a",
            },
          };
          this.trailPool.set(track_id, trailFeature);
        } else {
          (trailFeature.geometry as GeoJSON.LineString).coordinates =
            activeRenderPath;
        }
        trailsFeatures.push(trailFeature);
      }
    }

    try {
      if (pointsSourceNeedsSync) {
        aircraftSource.setData({
          type: "FeatureCollection",
          features: aircraftFeatures,
        });
      }
      if (shouldFlushTrails) {
        trailsSource.setData({
          type: "FeatureCollection",
          features: trailsFeatures,
        });
        this.lastTrailsFlushTime = now;
      }
    } catch (err) {
      console.warn("MapLibre synchronization deferred:", err);
    }
  }

  private updateTracks(
    track_id: string,
    position: [number, number],
    now: number,
  ) {
    let history = this.tracks.get(track_id);
    if (!history) {
      history = [
        [position[0], position[1]],
        [position[0], position[1]],
      ];
      this.tracks.set(track_id, history);
      this.lastTrailAppendTime.set(track_id, now);
      return;
    }

    const lastAppend = this.lastTrailAppendTime.get(track_id) || 0;
    if (now - lastAppend >= TRAIL_APPEND_INTERVAL_MS) {
      history.push(position);
      this.lastTrailAppendTime.set(track_id, now);
      if (history.length > MAX_HISTORY) history.shift();
    }
  }

  private getTrackBearing(track: [number, number][] | undefined) {
    if (!track || track.length < 3) return 0;
    const last = track[track.length - 1]!;
    const prev = track[track.length - 3]!;
    return (
      (bearing(point([prev[0], prev[1]]), point([last[0], last[1]])) + 360) %
      360
    );
  }

  removeAircraft(track_id: string) {
    this.aircraftCache.delete(track_id);
    this.pendingUpdates.add(track_id);
    this.tracks.delete(track_id);
    this.lastTrailAppendTime.delete(track_id);
    this.featurePool.delete(track_id);
    this.trailPool.delete(track_id);
    this.numericIdMap.delete(track_id);
    this.shadowFeatureStateHashes.delete(track_id);
    this.currentlySelectedIds.delete(track_id);
  }

  public notifySelectedAircraftChanged() {
    this.eventBus.emit("aircraft:selected-aircraft-updated", {
      aircraft: this.getSelectedAircraft(),
    });
  }

  getSelectedTargets(): string[] | null {
    return this.selection.getSelected();
  }
  getSelection() {
    return this.selection;
  }
  getAllTargets(): Map<string, SkopaTarget> {
    return this.aircraftCache;
  }
  getAircraftCount(): number {
    return this.aircraftCache.size;
  }
  isSelected(track_id: string): boolean {
    return this.currentlySelectedIds.has(track_id);
  }

  getSelectedAircraft(): SkopaTarget[] {
    const selectedList: SkopaTarget[] = [];
    for (const id of this.currentlySelectedIds) {
      const target = this.aircraftCache.get(id);
      if (target) selectedList.push(target);
    }
    return selectedList;
  }

  destroy() {
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    this.selection.destroy();
    this.aircraftCache.clear();
    this.pendingUpdates.clear();
    this.tracks.clear();
    this.lastTrailAppendTime.clear();
    this.featurePool.clear();
    this.trailPool.clear();
    this.numericIdMap.clear();
    this.shadowFeatureStateHashes.clear();
    this.currentlySelectedIds.clear();
  }
}
